import type { Booking } from "@/types/Booking";
import { endTimeToMinutes } from "@/utils/bookingTime";

const BUFFER_MINUTES = 15;
const SLOT_INCREMENT = 30;
const DAY_MINUTES = 24 * 60;

/**
 * Convert HHmm time string to minutes since midnight.
 */
function timeToMinutes(time: string): number {
  const hours = parseInt(time.slice(0, 2), 10);
  const minutes = parseInt(time.slice(2), 10);
  return hours * 60 + minutes;
}

/**
 * Convert minutes since midnight to HHmm time string.
 */
function minutesToTime(minutes: number): string {
  const h = Math.floor(minutes / 60)
    .toString()
    .padStart(2, "0");
  const m = (minutes % 60).toString().padStart(2, "0");
  return `${h}${m}`;
}

export type AvailableSlot = {
  startTime: string;
  endTime: string;
};

/**
 * Calculate available time slots for moving a booking within the same room and date.
 *
 * Mirrors the backend conflict logic in ember/src/utils/bookingValidation.ts
 * (checkRoomBookingConflict). The can-move endpoint remains the source of truth;
 * this is a client-side optimisation for UX.
 */
export function calculateAvailableSlots(
  existingBookings: Booking[],
  currentBooking: Booking,
  targetDate: Date,
  // The current booking's organisation party — when supplied, the org's own bookings may sit
  // adjacent (buffer waived), exactly as the same member's. Omit for non-org bookings.
  sameOrgMemberIds?: ReadonlySet<string>,
): AvailableSlot[] {
  const duration = endTimeToMinutes(currentBooking.endTime, currentBooking.startTime) - timeToMinutes(currentBooking.startTime);
  if (duration <= 0) return [];

  const currentHasBuffers = currentBooking.hasBuffers ?? true;
  const currentMemberId = currentBooking.memberId;

  // Filter to relevant bookings: same room, not cancelled, not the booking being moved
  const relevantBookings = existingBookings.filter((b) => {
    if (b.id === currentBooking.id) return false;
    if (b.isCancelled) return false;
    if (b.roomId !== currentBooking.roomId) return false;
    return true;
  });

  // Pre-compute blocked ranges for each relevant booking
  const blockedRanges = relevantBookings.map((b) => {
    const start = timeToMinutes(b.startTime);
    const end = endTimeToMinutes(b.endTime, b.startTime);
    const bookingHasBuffers = b.hasBuffers ?? true;
    // Same member OR same organisation counts as the same party — an org's own bookings may butt.
    const isSameMember = b.memberId === currentMemberId || (sameOrgMemberIds?.has(b.memberId) ?? false);
    return { start, end, bookingHasBuffers, isSameMember, memberId: b.memberId };
  });

  // Check if target date is today — if so, filter out past slots
  const now = new Date();
  const isToday =
    targetDate.getFullYear() === now.getFullYear() &&
    targetDate.getMonth() === now.getMonth() &&
    targetDate.getDate() === now.getDate();
  const currentMinutes = isToday ? now.getHours() * 60 + now.getMinutes() : 0;

  // Current booking's time — skip offering "move to where you already are"
  const currentStart = timeToMinutes(currentBooking.startTime);
  const currentEnd = endTimeToMinutes(currentBooking.endTime, currentBooking.startTime);
  const isSameDate =
    targetDate.getFullYear() === new Date(currentBooking.date).getFullYear() &&
    targetDate.getMonth() === new Date(currentBooking.date).getMonth() &&
    targetDate.getDate() === new Date(currentBooking.date).getDate();

  const slots: AvailableSlot[] = [];

  for (let candidateStart = 0; candidateStart + duration <= DAY_MINUTES; candidateStart += SLOT_INCREMENT) {
    const candidateEnd = candidateStart + duration;

    // Skip past slots if today
    if (isToday && candidateStart < currentMinutes) continue;

    // Skip current time slot on same date
    if (isSameDate && candidateStart === currentStart && candidateEnd === currentEnd) continue;

    // Check against each existing booking
    let hasConflict = false;
    for (const blocked of blockedRanges) {
      // Core time overlap — always a conflict
      if (candidateStart < blocked.end && candidateEnd > blocked.start) {
        hasConflict = true;
        break;
      }

      // Same-member adjacent bookings are allowed
      if (blocked.isSameMember) {
        const bufferStart = blocked.bookingHasBuffers ? blocked.start - BUFFER_MINUTES : blocked.start;
        const bufferEnd = blocked.bookingHasBuffers ? blocked.end + BUFFER_MINUTES : blocked.end;

        // Check if candidate ends within the buffer zone before the existing booking
        const endsBeforeExisting = candidateEnd > bufferStart && candidateEnd <= blocked.start;
        // Check if candidate starts within the buffer zone after the existing booking
        const startsAfterExisting = candidateStart >= blocked.end && candidateStart < bufferEnd;

        if (endsBeforeExisting || startsAfterExisting) {
          continue; // Adjacent — allowed for same member
        }
      }

      // Buffer overlap check
      const proposedBufferStart = currentHasBuffers ? candidateStart - BUFFER_MINUTES : candidateStart;
      const proposedBufferEnd = currentHasBuffers ? candidateEnd + BUFFER_MINUTES : candidateEnd;
      const blockedBufferStart = blocked.bookingHasBuffers ? blocked.start - BUFFER_MINUTES : blocked.start;
      const blockedBufferEnd = blocked.bookingHasBuffers ? blocked.end + BUFFER_MINUTES : blocked.end;

      if (proposedBufferStart < blockedBufferEnd && proposedBufferEnd > blockedBufferStart) {
        hasConflict = true;
        break;
      }
    }

    if (!hasConflict) {
      slots.push({
        startTime: minutesToTime(candidateStart),
        endTime: minutesToTime(candidateEnd),
      });
    }
  }

  return slots;
}
